// Firebase Configuration
import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore, initializeFirestore, memoryLocalCache } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getFunctions } from 'firebase/functions';
import { getMessaging, isSupported } from 'firebase/messaging';

const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase services
export const auth = getAuth(app);

// Initialize Firestore with memory cache to avoid internal assertion errors
// (persistentLocalCache + multipleTabManager causes crashes with many concurrent listeners)
export const db = initializeFirestore(app, {
    localCache: memoryLocalCache()
});

export const storage = getStorage(app);
export const functions = getFunctions(app);

// Initialize Firebase Cloud Messaging (only if supported)
let messaging: ReturnType<typeof getMessaging> | null = null;
isSupported().then((supported) => {
    if (supported) {
        messaging = getMessaging(app);
    }
});

// Suppress known Firestore internal assertion errors (ca9, etc.)
// These are transient SDK issues that don't affect app functionality
// Only set up after Firebase is initialized
if (typeof window !== 'undefined') {
    const originalError = console.error;
    console.error = function (...args: any[]) {
        if (args[0]?.includes?.('INTERNAL ASSERTION FAILED') && args[0]?.includes?.('ca9')) {
            // Silently ignore known Firestore state assertion errors
            return;
        }
        originalError.apply(console, args);
    };

    const originalWindowError = window.onerror;
    window.onerror = function (msg: any, url: any, lineNo: any, colNo: any, error: any) {
        if (typeof msg === 'string' && msg.includes('Unexpected state') && msg.includes('ca9')) {
            // Suppress known Firestore ca9 errors
            return true;
        }
        if (typeof originalWindowError === 'function') {
            return originalWindowError(msg, url, lineNo, colNo, error);
        }
        return false;
    };

    // Also handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
        if (event.reason?.message?.includes?.('Unexpected state') ||
            event.reason?.message?.includes?.('ca9') ||
            String(event.reason).includes('ca9')) {
            event.preventDefault();
        }
    });
}

export { messaging };
export default app;
